import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Icon} from '@drivesally/sally-ui';
import {grey} from '@drivesally/sally-ui/lib/libs/colors';

import {list as listDocumentsApi} from '@drivesally/sally-fn/lib/document/api';

import everythingFromApi from '../../../libs/everythingFromApi';

const ROW_PADDING = '10px';
const INNER_PADDING = '40px';
const EMPTY_COLOR = '#8EA1AC';
const ACTIVE_COLOR = '#263238';
const ICON_ACTIVE = '#04A9F4';

const FolderRow = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-bottom: 1px solid ${grey};
  cursor: ${({empty}) => (empty ? 'default' : 'pointer')};
  display: flex;
  font-size: 12px;
  gap: 10px;
  padding: ${ROW_PADDING};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({empty}) => (empty ? 'none' : grey)};
  }
`;

const FolderIcon = styled.span`
  color: ${({empty}) => (empty ? EMPTY_COLOR : ICON_ACTIVE)};
  font-size: 24px;
  line-height: 1;
  width: 24px;
`;

const FolderName = styled.span`
  color: ${({empty}) => (empty ? EMPTY_COLOR : ACTIVE_COLOR)};
  flex: 1;
`;

const FileRow = styled.button`
  align-items: center;
  background: ${({selected}) => (selected ? '#E3F4FD' : 'none')};
  border: none;
  border-bottom: 1px solid ${grey};
  color: ${ACTIVE_COLOR};
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 10px;
  padding: ${ROW_PADDING} ${ROW_PADDING} ${ROW_PADDING} ${INNER_PADDING};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({selected}) => (selected ? '#E3F4FD' : grey)};
  }
`;

const FileIcon = styled.span`
  color: ${EMPTY_COLOR};
  font-size: 16px;
  line-height: 1;
  width: 16px;
`;

const CrmDocumentBrowser = ({entityId, entityType, onSelect, predefinedDocs, selectedDocId, visible}) => {
  const [documents, setDocuments] = useState([]);
  const [openFolders, setOpenFolders] = useState([]);

  useEffect(() => {
    if (!entityId || !visible) return;
    let mounted = true;
    setDocuments([]);
    everythingFromApi(listDocumentsApi, {[entityType]: entityId, ordering: '-id'}, {shouldContinue: () => mounted})
      .then((results) => {
        if (mounted) setDocuments(results || []);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [entityId, entityType, visible]);

  const toggleFolder = (name) =>
    setOpenFolders((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  const groupedFolders = (predefinedDocs?.uiDocs || []).reduce((acc, folder) => {
    const existing = acc.find((f) => f.name === folder.name);
    if (existing) {
      existing.keys.push(folder.key);
    } else {
      acc.push({name: folder.name, keys: [folder.key]});
    }
    return acc;
  }, []);

  return (
    <div>
      {groupedFolders.map((folder) => {
        const folderFiles = documents.filter((doc) => folder.keys.includes(doc.type));
        const isEmpty = folderFiles.length === 0;
        const isOpen = openFolders.includes(folder.name);
        return (
          <div key={folder.name}>
            <FolderRow
              empty={isEmpty}
              onClick={isEmpty ? undefined : () => toggleFolder(folder.name)}
            >
              <FolderIcon empty={isEmpty}>
                <Icon icon={isOpen ? 'folderopen' : 'folder1'} />
              </FolderIcon>
              <FolderName empty={isEmpty}>{folder.name}</FolderName>
            </FolderRow>
            {isOpen && folderFiles.map((doc) => (
              <FileRow
                key={doc.id}
                selected={selectedDocId === doc.id}
                onClick={() => onSelect && onSelect(doc)}
              >
                <FileIcon><Icon icon="file1" /></FileIcon>
                {doc.name}
              </FileRow>
            ))}
          </div>
        );
      })}
    </div>
  );
};

CrmDocumentBrowser.propTypes = {
  entityId: PropTypes.number,
  entityType: PropTypes.string,
  onSelect: PropTypes.func,
  predefinedDocs: PropTypes.object,
  selectedDocId: PropTypes.number,
  visible: PropTypes.bool,
};

CrmDocumentBrowser.defaultProps = {
  visible: true,
};

export default CrmDocumentBrowser;
